import { clamp, round0 } from './formatters'

export type PackageType = '' | 'advantage'
export type BuildPayType = '' | 'upfront' | 'two'
export type RevenueCadence = 'one-time' | 'monthly' | 'quarterly'
// Pipeline management, charged per $2,500 bucket of monthly ad spend.
export const PIPELINE_BUCKET_FEE = 1000
// Optional corporate recruiter add-on: charged per $2,500 bucket of monthly ad
// spend, plus a one-time addition to the implementation fee.
export const RECRUITER_MONTHLY_FEE = 1500
export const RECRUITER_BUILD_UPCHARGE = 1500
// Milestone bonuses paid per rep still active in the field.
export const BONUS_DAY_15 = 500
export const BONUS_DAY_45 = 1000
export const BONUS_DAY_90 = 1000
// Leader campaign: two-month pilot that runs before the standard campaign.
export const LEADER_CAMPAIGN_MONTHS = 2
export const LEADER_CAMPAIGN_MONTHLY_FEE = 2000
// Charged in the bonuses section for every month the leader is active.
export const LEADER_ACTIVE_MONTHLY_FEE = 2000
export const LEADER_CAMPAIGN_AD_SPEND = [5000, 5000]
export const LEADER_CAMPAIGN_CPH = 10000
export const LEADER_CAMPAIGN_HIRES = 1
export const LEADER_CAMPAIGN_BUILD_UPCHARGE = 1000
export type IndustryType =
  | ''
  | 'Windows'
  | 'Roofing'
  | 'Solar (Sales)'
  | 'Solar (Vertical)'
  | 'Solar (Community)'
  | 'Pest'
  | 'Fiber'
  | 'Deregulated Energy'
  | 'Hardscaping'
  | 'Landscaping'
  | 'Other'

export interface Inputs {
  companyName: string
  pkg: PackageType
  buildPay: BuildPayType
  industry: IndustryType
  campaigns: number
  monthlyCampaigns?: number[] // Array of 12 numbers for monthly overrides
  adSpend: number
  monthlyAdSpend?: number[] // Array of 12 numbers for monthly ad spend overrides
  cph: number
  ticket: number
  gpm: number // 0-100 in state, converted to decimal for calc
  salesVetMonthly: number
  salesFirst90Total: number
  pctLaunched: number // 0-100
  pct30: number // 0-100
  pct90: number // 0-100 Day 30 to Day 90 retention
  pct1y: number // 0-100
  pullthrough: number // 0-100
  revenueCadence: RevenueCadence // New: one-time, monthly, or quarterly
  residualTicket?: number // New: monthly ticket for residual products
  monthlyAttrition?: number // New: monthly customer attrition (0-100)
  quarterlyAttrition?: number // New: quarterly customer attrition (0-100)
  monthlyBuildFees?: number[] // Array of 13 numbers for build fee overrides (Build + 12 months)
  leaderCampaign?: boolean // Two-month leader pilot that delays the standard campaign
  leaderMonth2Sales?: number // Optional override for Month 2 total sales during the leader campaign
  addRecruiter?: boolean // Adds the corporate recruiter monthly fee
  cphLow?: number // Low end of the ad spend cost-per-hire range
  cphHigh?: number // High end of the ad spend cost-per-hire range
  salesSchoolEnabled?: boolean // Enables milestone bonus charges
  revenueTarget?: number // 12-month gross revenue target for optimizer
}

export interface IndustryDefaults {
  ticket: number
  gpm: number
  salesFirst90Total: number
  salesVetMonthly: number
  revenueCadence: RevenueCadence
  residualTicket?: number
}

export const INDUSTRY_DEFAULTS: Record<IndustryType, IndustryDefaults> = {
  '': {
    ticket: 20000,
    gpm: 30,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Windows: {
    ticket: 20000,
    gpm: 30,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Roofing: {
    ticket: 20000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Sales)': {
    ticket: 40000,
    gpm: 10,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Vertical)': {
    ticket: 40000,
    gpm: 20,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Community)': {
    ticket: 700,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 350,
  },
  Pest: {
    ticket: 170,
    gpm: 40,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 85,
  },
  Fiber: {
    ticket: 100,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 50,
  },
  'Deregulated Energy': {
    ticket: 70,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 35,
  },
  Hardscaping: {
    ticket: 20000,
    gpm: 25,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Landscaping: {
    ticket: 50000,
    gpm: 30,
    salesFirst90Total: 5,
    salesVetMonthly: 8,
    revenueCadence: 'one-time',
  },
  Other: {
    ticket: 25000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
}

export interface PackagePreset {
  name: string
  buildTwoPayments: number
  buildUpfront: number
  systemMonthly: number
  campaignMonthly: number
  recruiterMonthly: number
  bonusLaunch: number
  bonusDay30: number
  bonusDay60: number
  bonusDay90: number
}

export const PACKAGE_PRESETS: Record<PackageType, PackagePreset> = {
  '': {
    name: 'No Package Selected',
    buildTwoPayments: 0,
    buildUpfront: 0,
    systemMonthly: 0,
    campaignMonthly: 0,
    recruiterMonthly: 0,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
  advantage: {
    name: 'HIRE Advantage',
    // $4,500 implementation fee, splittable into three even payments.
    buildTwoPayments: 1500,
    buildUpfront: 4500,
    // Flat monthly partnership fee.
    systemMonthly: 1500,
    // Pipeline management, charged per $2,500 bucket of monthly ad spend.
    campaignMonthly: PIPELINE_BUCKET_FEE,
    recruiterMonthly: 0,
    bonusLaunch: 150,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
}

export interface RowData {
  period: string
  activeCampaigns: number
  hires: number
  launched: number
  active30d: number
  active60d: number
  active90d: number
  veterans: number
  rampingReps: number
  veteranReps: number
  buildFee: number
  platformFee: number
  campaignFee: number
  recruiterFee: number
  leaderRevShare: number
  hireCost: number
  adSpend: number
  bonuses: number
  totalCost: number
  totalSales: number
  realizationRate: number
  grossRevenue: number
  grossProfit: number
  netProfit: number
  cumProfit: number
  residualRevenue?: number
  totalResidualRevenue?: number
}

export interface CalculationResult {
  preset: PackagePreset
  hiresPerMonth: number
  launchedPerMonth: number
  gpPerSale: number
  totalHireCost: number
  totalAdSpend: number
  totalBonuses: number
  totalCostAll: number
  totalGrossProfit: number
  totalNetProfit: number
  roi: number
  breakeven: string
  rows: RowData[]
}

export const DEFAULT_INPUTS: Inputs = {
  companyName: '',
  pkg: '',
  buildPay: 'upfront',
  industry: '',
  campaigns: 2,
  monthlyCampaigns: Array(12).fill(2),
  adSpend: 2500,
  monthlyAdSpend: Array(12).fill(2500),
  cph: 350,
  ticket: 20000,
  gpm: 30,
  salesVetMonthly: 6,
  salesFirst90Total: 10,
  pctLaunched: 70,
  pct30: 40,
  pct90: 40,
  pct1y: 40,
  pullthrough: 80,
  revenueCadence: 'one-time',
  residualTicket: undefined,
  monthlyAttrition: 2,
  quarterlyAttrition: 5,
  monthlyBuildFees: undefined,
  leaderCampaign: false,
  addRecruiter: false,
  salesSchoolEnabled: true,
  revenueTarget: 0,
}

export function calculateModel(inputs: Inputs): CalculationResult {
  const months = 12
  const preset = PACKAGE_PRESETS[inputs.pkg]

  const gpm = clamp(inputs.gpm / 100, 0, 1)
  const pctLaunched = clamp(inputs.pctLaunched / 100, 0, 1)
  const pct30 = clamp(inputs.pct30 / 100, 0, 1)
  const pct90 = clamp(inputs.pct90 / 100, 0, 1)
  const pct1y = clamp(inputs.pct1y / 100, 0, 1)
  const pullthrough = clamp(inputs.pullthrough / 100, 0, 1)

  const pct60 = Math.sqrt(pct90)

  const monthlyCampaigns =
    inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12
      ? inputs.monthlyCampaigns
      : Array(12).fill(Math.max(1, inputs.campaigns))

  const monthlyAdSpendValues =
    inputs.monthlyAdSpend && inputs.monthlyAdSpend.length === 12
      ? inputs.monthlyAdSpend
      : Array(12).fill(Math.max(0, inputs.adSpend))

  const cph = Math.max(1, inputs.cph)

  // Leader campaign: a two-month pilot that delays the standard campaign by two
  // months. Months 1-2 charge a flat monthly fee, skip Sales School, and place a
  // single leader rep who sells on a fixed curve.
  const leaderCampaign = inputs.leaderCampaign === true
  const effectiveCampaigns = leaderCampaign
    ? [0, 0, ...monthlyCampaigns.slice(0, months - LEADER_CAMPAIGN_MONTHS)]
    : monthlyCampaigns
  const effectiveAdSpendPerCampaign = leaderCampaign
    ? [0, 0, ...monthlyAdSpendValues.slice(0, months - LEADER_CAMPAIGN_MONTHS)]
    : monthlyAdSpendValues

  const hired = effectiveCampaigns.map((campaigns, idx) => {
    const adSpendPerCampaign = effectiveAdSpendPerCampaign[idx]
    const totalAdSpend = adSpendPerCampaign * campaigns
    return round0(totalAdSpend / cph)
  })
  if (leaderCampaign) {
    // The leader is hired in Month 2.
    hired[1] = LEADER_CAMPAIGN_HIRES
  }

  // The leader rep is tracked separately from the standard funnel.
  const launched = hired.map((h, idx) =>
    leaderCampaign && idx < LEADER_CAMPAIGN_MONTHS ? 0 : h * pctLaunched,
  )

  const cohortDay30 = launched.map((x) => x * pct30)
  const cohortDay60 = cohortDay30.map((x) => x * pct60)
  const cohortDay90 = cohortDay60.map((x) => x * pct60)

  const active30d = Array.from({ length: months }, (_, m) => {
    return m >= 1 ? cohortDay30[m - 1] : 0
  })

  const active60d = Array.from({ length: months }, (_, m) => {
    return m >= 2 ? cohortDay60[m - 2] : 0
  })

  const active90d = Array.from({ length: months }, (_, m) => {
    return m >= 3 ? cohortDay90[m - 3] : 0
  })

  const monthlyVeteranRetentionRate = Math.pow(pct1y, 1 / 9)

  const veterans: number[] = []
  for (let m = 0; m < months; m++) {
    if (m <= 3) {
      veterans.push(0)
    } else {
      const previousVeterans = veterans[m - 1] * monthlyVeteranRetentionRate
      const newGraduates = m >= 4 ? cohortDay90[m - 4] : 0
      veterans.push(previousVeterans + newGraduates)
    }
  }

  // The leader rep launches in Month 2 and then ages through the retention
  // stages: 30d+ in Month 3, 60d+ in Month 4, 90d+ in Month 5.
  const leaderAt = (m: number, monthIndex: number) =>
    leaderCampaign && m === monthIndex ? LEADER_CAMPAIGN_HIRES : 0
  const leaderLaunched = Array.from({ length: months }, (_, m) =>
    leaderAt(m, 1),
  )
  const leaderActive30d = Array.from({ length: months }, (_, m) =>
    leaderAt(m, 2),
  )
  const leaderActive60d = Array.from({ length: months }, (_, m) =>
    leaderAt(m, 3),
  )
  const leaderActive90d = Array.from({ length: months }, (_, m) =>
    leaderAt(m, 4),
  )
  // The leader sells at 75% of veteran output in Month 2 and full veteran
  // output in Months 3-4, then their sales stop.
  const leaderSalesMultipliers = [0, 0.75, 1, 1]


  const salesPer30Days = Math.max(0, inputs.salesFirst90Total) / 3

  // Rep counts that actually drive sales, tracked so the UI can explain the math.
  const rampingReps = Array.from({ length: months }, (_, m) => {
    let reps = cohortDay90[m]
    if (m >= 1) reps += cohortDay90[m - 1]
    if (m >= 2) reps += cohortDay90[m - 2]
    return reps
  })

  const veteranReps = Array.from({ length: months }, (_, m) => {
    let reps = 0
    if (m >= 3) {
      for (let cohortMonth = 0; cohortMonth <= m - 3; cohortMonth++) {
        let veteranCount = cohortDay90[cohortMonth]
        const monthsPast90 = m - cohortMonth - 3
        for (let ageMonth = 0; ageMonth < monthsPast90; ageMonth++) {
          veteranCount *= monthlyVeteranRetentionRate
        }
        reps += veteranCount
      }
    }
    // The leader rep sells at a fraction of a veteran's rate while active.
    return reps + (leaderSalesMultipliers[m] ?? 0) * (leaderCampaign ? 1 : 0)
  })

  const totalSales = Array.from({ length: months }, (_, m) => {
    // Month 2 of the leader campaign can be overridden directly.
    if (
      leaderCampaign &&
      m === 1 &&
      inputs.leaderMonth2Sales !== undefined &&
      inputs.leaderMonth2Sales !== null
    ) {
      return Math.max(0, inputs.leaderMonth2Sales)
    }
    return (
      rampingReps[m] * salesPer30Days +
      veteranReps[m] * Math.max(0, inputs.salesVetMonthly)
    )
  })

  const realizedSales = totalSales.map((x) => x * pullthrough)

  const residualRevenue: number[] = []
  const totalResidualRevenue: number[] = []

  if (inputs.revenueCadence === 'monthly' && inputs.residualTicket) {
    const monthlyAttritionRate = clamp(
      (inputs.monthlyAttrition || 2) / 100,
      0,
      1,
    )
    const customerRetention = 1 - monthlyAttritionRate

    const newMonthlyRevenue = realizedSales.map(
      (sales) => sales * Math.max(0, inputs.ticket),
    )

    for (let m = 0; m < months; m++) {
      let monthlyResidual = 0

      if (m >= 1) {
        for (let sourceMonth = 0; sourceMonth <= m - 1; sourceMonth++) {
          const monthsGeneratingResidual = m - sourceMonth - 1
          const retentionFactor = Math.pow(
            customerRetention,
            monthsGeneratingResidual,
          )
          monthlyResidual +=
            (newMonthlyRevenue[sourceMonth] / Math.max(0, inputs.ticket)) *
            retentionFactor *
            inputs.residualTicket
        }
      }

      residualRevenue.push(monthlyResidual)
      totalResidualRevenue.push(
        realizedSales[m] * Math.max(0, inputs.ticket) + monthlyResidual,
      )
    }
  } else if (inputs.revenueCadence === 'quarterly' && inputs.residualTicket) {
    const quarterlyAttritionRate = clamp(
      (inputs.quarterlyAttrition || 5) / 100,
      0,
      1,
    )
    const quarterlyRetention = 1 - quarterlyAttritionRate

    const newMonthlyRevenue = realizedSales.map(
      (sales) => sales * Math.max(0, inputs.ticket),
    )

    for (let m = 0; m < months; m++) {
      let quarterlyResidual = 0

      if (m >= 3) {
        const sourceMonth = m - 3
        quarterlyResidual =
          (newMonthlyRevenue[sourceMonth] / Math.max(0, inputs.ticket)) *
          quarterlyRetention *
          inputs.residualTicket
      }

      residualRevenue.push(quarterlyResidual)
      totalResidualRevenue.push(
        realizedSales[m] * Math.max(0, inputs.ticket) + quarterlyResidual,
      )
    }
  } else {
    for (let m = 0; m < months; m++) {
      residualRevenue.push(0)
      totalResidualRevenue.push(realizedSales[m] * Math.max(0, inputs.ticket))
    }
  }

  const grossRevenue = totalResidualRevenue
  const grossProfit = grossRevenue.map((x) => x * gpm)
  const gpPerSale = Math.max(0, inputs.ticket) * gpm

  // Implementation is a flat one-time fee regardless of ad spend.
  const defaultBuildLaunch = inputs.pkg
    ? preset.buildUpfront +
      (leaderCampaign ? LEADER_CAMPAIGN_BUILD_UPCHARGE : 0) +
      (inputs.addRecruiter ? RECRUITER_BUILD_UPCHARGE : 0)
    : 0

  // Check for custom build fees - but ignore them entirely when no package is selected
  const customBuildFees = inputs.pkg ? inputs.monthlyBuildFees : undefined

  // Build row fee - use custom if set, otherwise use default
  const buildRowFee =
    customBuildFees &&
    customBuildFees[0] !== undefined &&
    customBuildFees[0] !== null
      ? customBuildFees[0]
      : defaultBuildLaunch

  const systemMonthly = preset.systemMonthly
  // The service fee scales with each $2,500 bucket of monthly ad spend.
  // Leader campaign months bill a single flat monthly fee instead.
  const isLeaderMonth = (idx: number) =>
    leaderCampaign && idx < LEADER_CAMPAIGN_MONTHS
  const campaignFees = effectiveCampaigns.map((buckets, idx) =>
    isLeaderMonth(idx) ? 0 : preset.campaignMonthly * buckets,
  )
  // An optional corporate recruiter is billed as a flat monthly add-on.
  const recruiterFees = effectiveCampaigns.map((buckets, idx) =>
    inputs.addRecruiter && inputs.pkg && !isLeaderMonth(idx)
      ? RECRUITER_MONTHLY_FEE * buckets
      : 0,
  )

  // Milestone bonuses are earned by reps who reach the NEXT retention milestone,
  // so the $500 counts reps who make it to day 30, the first $1,000 counts reps
  // who make it to day 60, and the final $1,000 counts reps who make it to day
  // 90. Counts round up because nobody pays a partial bonus and some reps who
  // qualified at day 15 or day 45 fall off shortly after.
  const bonuses = Array.from({ length: months }, (_, m) => {
    if (!inputs.pkg || inputs.salesSchoolEnabled === false) return 0
    // No bonuses during the leader campaign months.
    if (isLeaderMonth(m)) return 0
    const day15 = Math.ceil(cohortDay30[m]) * BONUS_DAY_15
    const day45 = m >= 1 ? Math.ceil(cohortDay60[m - 1]) * BONUS_DAY_45 : 0
    // The day 90 count rounds to the nearest whole rep.
    const day90 = m >= 3 ? Math.round(cohortDay90[m - 3]) * BONUS_DAY_90 : 0
    return day15 + day45 + day90
  })

  // While the leader is active — every month after the two-month search — an
  // additional flat monthly fee is billed alongside the bonuses.
  const leaderActiveFees = Array.from({ length: months }, (_, m) =>
    leaderCampaign && inputs.pkg && m >= LEADER_CAMPAIGN_MONTHS
      ? LEADER_ACTIVE_MONTHLY_FEE
      : 0,
  )
  const bonusesWithLeader = bonuses.map(
    (bonus, idx) => bonus + leaderActiveFees[idx],
  )

  // Monthly build fees - use custom if set, otherwise default to 0 for all months
  const buildFees = Array(months)
    .fill(0)
    .map((_, i) => {
      // Check for custom build fee (index i+1 because index 0 is Build row)
      if (
        customBuildFees &&
        customBuildFees[i + 1] !== undefined &&
        customBuildFees[i + 1] !== null
      ) {
        return customBuildFees[i + 1]
      }
      // Default: no build fees in monthly rows
      return 0
    })

  // Partnership fee - set to 0 when there is no ad spend that month.
  // Leader campaign months bill a flat $2,500 total instead.
  const platformFees = effectiveCampaigns.map((campaigns, idx) => {
    if (isLeaderMonth(idx)) return inputs.pkg ? LEADER_CAMPAIGN_MONTHLY_FEE : 0
    return campaigns === 0 ? 0 : systemMonthly
  })

  const hireCost = Array(months)
    .fill(0)
    .map(
      (_, i) =>
        buildFees[i] + platformFees[i] + campaignFees[i] + recruiterFees[i],
    )

  const adSpend = effectiveCampaigns.map((c, idx) =>
    isLeaderMonth(idx)
      ? LEADER_CAMPAIGN_AD_SPEND[idx]
      : effectiveAdSpendPerCampaign[idx] * c,
  )

  const totalCost = Array(months)
    .fill(0)
    .map((_, i) => hireCost[i] + adSpend[i] + bonusesWithLeader[i])
  const netProfit = grossProfit.map((x, i) => x - totalCost[i])

  const rows: RowData[] = []
  let cum = -buildRowFee

  rows.push({
    period: 'Build',
    activeCampaigns: 0,
    hires: 0,
    launched: 0,
    active30d: 0,
    active60d: 0,
    active90d: 0,
    veterans: 0,
    rampingReps: 0,
    veteranReps: 0,
    buildFee: buildRowFee,
    platformFee: 0,
    campaignFee: 0,
    recruiterFee: 0,
    leaderRevShare: 0,
    hireCost: buildRowFee,
    adSpend: 0,
    bonuses: 0,
    totalCost: buildRowFee,
    totalSales: 0,
    realizationRate: pullthrough,
    grossRevenue: 0,
    grossProfit: 0,
    netProfit: -buildRowFee,
    cumProfit: cum,
    residualRevenue: 0,
    totalResidualRevenue: 0,
  })

  for (let i = 0; i < months; i++) {
    cum += netProfit[i]
    rows.push({
      period: `Month ${i + 1}`,
      activeCampaigns: effectiveCampaigns[i],
      hires: hired[i],
      launched: launched[i] + leaderLaunched[i],
      active30d: active30d[i] + leaderActive30d[i],
      active60d: active60d[i] + leaderActive60d[i],
      active90d: active90d[i] + leaderActive90d[i],
      veterans: veterans[i],
      rampingReps: rampingReps[i],
      veteranReps: veteranReps[i],
      buildFee: buildFees[i],
      platformFee: platformFees[i],
      campaignFee: campaignFees[i],
      recruiterFee: recruiterFees[i],
      leaderRevShare: 0,
      hireCost: hireCost[i],
      adSpend: adSpend[i],
      bonuses: bonusesWithLeader[i],
      totalCost: totalCost[i],
      totalSales: totalSales[i],
      realizationRate: pullthrough,
      grossRevenue: grossRevenue[i],
      grossProfit: grossProfit[i],
      netProfit: netProfit[i],
      cumProfit: cum,
      residualRevenue: residualRevenue[i],
      totalResidualRevenue: totalResidualRevenue[i],
    })
  }

  const totalHireCost = buildRowFee + hireCost.reduce((a, b) => a + b, 0)
  const totalAdSpend = adSpend.reduce((a, b) => a + b, 0)
  const totalBonuses = bonusesWithLeader.reduce((a, b) => a + b, 0)
  const totalCostAll = totalHireCost + totalAdSpend + totalBonuses
  const totalGrossProfit = grossProfit.reduce((a, b) => a + b, 0)
  const totalNetProfit =
    totalGrossProfit -
    (hireCost.reduce((a, b) => a + b, 0) + totalAdSpend + totalBonuses) -
    buildRowFee
  const roi = totalCostAll > 0 ? (totalNetProfit / totalCostAll) * 100 : 0

  let breakeven = 'Not within 12 months'
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].cumProfit >= 0) {
      breakeven = rows[i].period
      break
    }
  }

  return {
    preset,
    hiresPerMonth: hired[0],
    launchedPerMonth: launched[0],
    gpPerSale,
    totalHireCost,
    totalAdSpend,
    totalBonuses,
    totalCostAll,
    totalGrossProfit,
    totalNetProfit,
    roi,
    breakeven,
    rows,
  }
}

// Active reps for a month, matching the projection table's
// "Active Reps (Average)" metric.
export function activeRepsForRow(row: RowData): number {
  return (
    row.launched * 0.5 +
    row.active30d +
    row.active60d +
    row.active90d +
    row.veterans
  )
}

function generateCampaignPatterns(): number[][] {
  const candidates: number[][] = []

  // 1. Flat patterns — extended to a high ceiling so large targets are always
  // reachable by simply running more campaigns each month.
  const MAX_CAMPAIGNS = 40
  for (let n = 1; n <= MAX_CAMPAIGNS; n += 1) {
    candidates.push(Array(12).fill(n))
  }

  // 1b. Front-load then taper, scaled up to the high ceiling (for cheaper ways
  // to hit large targets than a flat high pattern).
  for (let peak = 6; peak <= MAX_CAMPAIGNS; peak += 1) {
    for (const plateauMonths of [2, 3, 4, 6]) {
      const pattern: number[] = []
      for (let m = 0; m < 12; m++) {
        if (m < plateauMonths) {
          pattern.push(peak)
        } else {
          const tapered = peak - (m - plateauMonths + 1)
          pattern.push(Math.max(1, tapered))
        }
      }
      candidates.push(pattern)
    }
  }

  // 2. Ramp-up then plateau
  for (let start = 1; start <= 3; start += 1) {
    for (let peak = start; peak <= 5; peak += 1) {
      if (peak === start) continue
      for (let rampMonth = 1; rampMonth <= 6; rampMonth++) {
        const pattern: number[] = []
        for (let m = 0; m < 12; m++) {
          pattern.push(m < rampMonth ? start : peak)
        }
        candidates.push(pattern)
      }
    }
  }

  // 3. Front-load then taper down to the 1x floor
  for (let peak = 2; peak <= 5; peak += 1) {
    for (let plateauMonths = 1; plateauMonths <= 9; plateauMonths++) {
      const pattern: number[] = []
      for (let m = 0; m < 12; m++) {
        if (m < plateauMonths) {
          pattern.push(peak)
        } else {
          const tapered = peak - (m - plateauMonths + 1)
          pattern.push(Math.max(1, tapered))
        }
      }
      candidates.push(pattern)
    }
  }

  // 4. Gradual ramp in whole campaign increments
  for (let start = 1; start <= 2; start += 1) {
    for (let stepMonths = 1; stepMonths <= 3; stepMonths++) {
      for (let cap = start + 1; cap <= 5; cap += 1) {
        const pattern: number[] = []
        let cur = start
        for (let m = 0; m < 12; m++) {
          pattern.push(Math.min(cap, cur))
          if ((m + 1) % stepMonths === 0) cur = Math.min(cap, cur + 1)
        }
        candidates.push(pattern)
      }
    }
  }

  return candidates
}

export function optimizeCampaignsForRevenueTarget(
  inputs: Inputs,
  target: number,
  months: number = 12,
): number[] | null {
  if (!target || target <= 0) return null
  const horizon = Math.max(1, Math.min(12, months))
  const candidates = generateCampaignPatterns()

  // Cheapest pattern that MEETS or exceeds the target within the horizon
  let best: { pattern: number[]; cost: number } | null = null
  // Fallback: pattern producing the MOST revenue in the horizon (used when the
  // target can't be reached in the window, e.g. a short 3/6-month horizon)
  let maxRev: { pattern: number[]; revenue: number } | null = null

  for (const candidate of candidates) {
    // Constraint: first month must be >= 1.0 (no half-campaign start)
    if (candidate[0] < 1.0) continue

    const result = calculateModel({
      ...inputs,
      monthlyCampaigns: candidate,
      campaigns: candidate[0],
    })

    let cumGrossRevenue = 0
    let cumTotalCost = 0
    for (let i = 0; i <= horizon; i++) {
      cumGrossRevenue += result.rows[i].grossRevenue
      cumTotalCost += result.rows[i].totalCost
    }

    if (cumGrossRevenue >= target) {
      if (!best || cumTotalCost < best.cost) {
        best = { pattern: candidate, cost: cumTotalCost }
      }
    }

    if (!maxRev || cumGrossRevenue > maxRev.revenue) {
      maxRev = { pattern: candidate, revenue: cumGrossRevenue }
    }
  }

  // Prefer the cheapest pattern that hits the target; otherwise get as close as
  // possible (the highest-revenue pattern achievable for this horizon).
  if (best) return best.pattern
  return maxRev ? maxRev.pattern : null
}

// Finds the cheapest campaign pattern that reaches a target active-rep count
// (net team growth) in the final month of the horizon.
export function optimizeCampaignsForTeamTarget(
  inputs: Inputs,
  target: number,
  months: number = 12,
): number[] | null {
  if (!target || target <= 0) return null
  const horizon = Math.max(1, Math.min(12, months))
  const candidates = generateCampaignPatterns()

  let best: { pattern: number[]; cost: number } | null = null
  let maxTeam: { pattern: number[]; team: number } | null = null

  for (const candidate of candidates) {
    if (candidate[0] < 1.0) continue

    const result = calculateModel({
      ...inputs,
      monthlyCampaigns: candidate,
      campaigns: candidate[0],
    })

    const activeReps = activeRepsForRow(result.rows[horizon])

    let cumTotalCost = 0
    for (let i = 0; i <= horizon; i++) {
      cumTotalCost += result.rows[i].totalCost
    }

    if (activeReps >= target) {
      if (!best || cumTotalCost < best.cost) {
        best = { pattern: candidate, cost: cumTotalCost }
      }
    }

    if (!maxTeam || activeReps > maxTeam.team) {
      maxTeam = { pattern: candidate, team: activeReps }
    }
  }

  if (best) return best.pattern
  return maxTeam ? maxTeam.pattern : null
}
